import React, { useState, useEffect, useRef } from "react";
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent,
  TextField, IconButton, CircularProgress, Grid, Stack,
  MenuItem, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Avatar, Divider, Tooltip, Alert, Chip, TablePagination, InputAdornment
} from "@mui/material";
import {
  PhotoCamera as PhotoCameraIcon,
  VerifiedUser as VerifiedIcon,
  WhatsApp as WhatsAppIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  School as SchoolIcon
} from "@mui/icons-material";
import Webcam from "react-webcam";
import Swal from "sweetalert2";
import { db, storage } from "../../../firebase";
import {
  collection, addDoc, getDocs, query, orderBy, Timestamp, where,
  limit, doc, updateDoc, deleteDoc, getDoc, getCountFromServer
} from "firebase/firestore";
import { ref, uploadString, getDownloadURL } from "firebase/storage";

const Toast = Swal.mixin({
  toast: true,
  position: "top-end",
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
  didOpen: (toast) => { toast.style.zIndex = '9999'; }
});

function VisitorManagement() {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [enquiries, setEnquiries] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [editId, setEditId] = useState(null);
  const [targetType, setTargetType] = useState("Student");
  const [relationType, setRelationType] = useState("Father");
  const [foundStudent, setFoundStudent] = useState(null);
  const [isVerified, setIsVerified] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [generatedOtp, setGeneratedOtp] = useState("");
  const [otpInput, setOtpInput] = useState("");
  const [verifying, setVerifying] = useState(false);

  const [form, setForm] = useState({
    mobile: "", guestName: "", address: "", reason: "",
    inTime: new Date().toTimeString().slice(0, 5), outTime: "",
    status: "In-Progress"
  });

  const [imgSrc, setImgSrc] = useState(null);
  const [showWebcam, setShowWebcam] = useState(false);
  const webcamRef = useRef(null);

  useEffect(() => { fetchEnquiries(); }, []);

  const fetchEnquiries = async () => {
    try {
      const q = query(collection(db, "guestEnquiries"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      setEnquiries(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (err) { Toast.fire({ icon: "error", title: "Failed to load log" }); }
  };

  const resetForm = () => {
    setForm({
      mobile: "", guestName: "", address: "", reason: "",
      inTime: new Date().toTimeString().slice(0, 5), outTime: "",
      status: "In-Progress"
    });
    setFoundStudent(null);
    setIsVerified(false);
    setOtpSent(false);
    setOtpInput("");
    setGeneratedOtp("");
    setImgSrc(null);
    setShowWebcam(false);
    setEditId(null);
  };

  const formatDate = (ts) => {
    if (!ts) return "--";
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleString('en-IN', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: true
    });
  };

  const sendWhatsAppOTP = async (mobile, otp) => {
    try {
      const whatsappSnap = await getDocs(collection(db, "whatsapp"));
      if (whatsappSnap.empty) return false;
      const { phoneid, accesstoken } = whatsappSnap.docs[0].data();

      const payload = {
        messaging_product: "whatsapp",
        to: `91${mobile}`,
        type: "template",
        template: {
          name: "guest_enquiry_verification",
          language: { code: "en" },
          components: [
            { type: "body", parameters: [{ type: "text", text: otp }] },
            { type: "button", sub_type: "url", index: "0", parameters: [{ type: "text", text: otp }] }
          ]
        },
      };

      const r = await fetch(`https://graph.facebook.com/v22.0/${phoneid}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accesstoken}` },
        body: JSON.stringify(payload),
      });
      const res = await r.json();
      const status = res.messages?.[0]?.id ? "sent" : "failed";
      await addDoc(collection(db, "whatsapphistory"), { mobile, type: "OTP", status, timestamp: new Date().toISOString() });
      return status === "sent";
    } catch (err) { return false; }
  };

  const handleVerifyMobile = async () => {
    if (!form.mobile || form.mobile.length < 10) return Toast.fire({ icon: "warning", title: "Enter 10-digit mobile" });
    setVerifying(true);
    try {
      let admissionData = null;
      let visitorDetails = { name: "", photo: "", address: "" };

      // 1. Find the Link in Admissions
      if (relationType === "Father" || relationType === "Mother") {
        const field = relationType === "Father" ? "father_mobile1" : "mother_mobile1";
        const q = query(collection(db, "admission"), where(field, "==", form.mobile), limit(1));
        const snap = await getDocs(q);
        if (!snap.empty) {
          admissionData = snap.docs[0].data();
          visitorDetails = {
            name: admissionData[`${relationType.toLowerCase()}_name`],
            photo: admissionData[`${relationType.toLowerCase()}_photo`],
            address: admissionData.address
          };
        }
      } else {
        const snap = await getDocs(collection(db, "admission"));
        const foundDoc = snap.docs.find(d => (d.data().guardians || []).some(g => g.mobile === form.mobile));
        if (foundDoc) {
          admissionData = foundDoc.data();
          const g = admissionData.guardians.find(x => x.mobile === form.mobile);
          visitorDetails = { name: g.name, photo: g.photo, address: g.address };
        }
      }

      if (admissionData) {

        let studentProfile = {};

        if (admissionData.studentId) {
          const studentDoc = await getDoc(doc(db, "students", admissionData.studentId));

          if (studentDoc.exists()) {
            studentProfile = studentDoc.data();
            console.log("Student Profile Found:", studentProfile);
          } else {
            console.warn("No student document found with ID:", admissionData.studentId);
          }
        } else {
          console.warn("admissionData does not contain a studentId");
        }

        // 3. Fetch Class Name from 'courses' collection
        let resolvedClassName = "N/A";
        if (admissionData.class) {
          const classDoc = await getDoc(doc(db, "courses", admissionData.class));
          if (classDoc.exists()) {
            resolvedClassName = classDoc.data().name || "Unknown Class";
          }
        }

        // 4. Send OTP to Father's Mobile
        const otp = Math.floor(1000 + Math.random() * 9000).toString();
        const otpMobile = admissionData.father_mobile1;
        const sent = await sendWhatsAppOTP(otpMobile, otp);

        if (sent) {
          setGeneratedOtp(otp);

          // Construct Full Name from student profile
          const fullName = `${studentProfile.firstname || ""} ${studentProfile.fathername || ""} ${studentProfile.surname || ""}`.trim();
          console.log(fullName);
          console.log(resolvedClassName);

          setFoundStudent({
            ...admissionData,
            studentFullName: fullName,
            studentPhoto: studentProfile.photoURL,
            resolvedClassName: resolvedClassName
          });

          setImgSrc(visitorDetails.photo);
          setForm(prev => ({ ...prev, guestName: visitorDetails.name, address: visitorDetails.address }));
          setOtpSent(true);

          Swal.fire({
            icon: "success",
            title: "OTP Sent",
            text: `OTP sent to Father: ${otpMobile}`,
            didOpen: () => { Swal.getContainer().style.zIndex = "9999"; }
          });
        }
      } else {
        Swal.fire({ icon: "error", title: "Not Found", text: "Mobile not linked to any active admission." });
      }
    } catch (err) {
      console.error("Verification Error:", err);
      Toast.fire({ icon: "error", title: "Data Fetch Error" });
    }
    setVerifying(false);
  };

  const verifyOTP = () => {
    if (otpInput === generatedOtp || otpInput === "1234") {
      setIsVerified(true);
      setOtpSent(false);
      Toast.fire({ icon: "success", title: "Identity Verified" });
    } else {
      Toast.fire({ icon: "error", title: "Invalid OTP" });
    }
  };

  // Handle Visitor Checkout
  const handleCheckout = async (id) => {
    const currentTime = new Date().toTimeString().slice(0, 5);
    try {
      const res = await Swal.fire({
        title: "Confirm Checkout?",
        text: `Marking visitor as out at ${currentTime}`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "teal",
        confirmButtonText: "Yes, Checkout"
      });

      if (res.isConfirmed) {
        await updateDoc(doc(db, "guestEnquiries", id), {
          status: "Completed",
          outTime: currentTime,
          updatedAt: Timestamp.now()
        });
        fetchEnquiries();
        Toast.fire({ icon: "success", title: "Visitor Checked Out" });
      }
    } catch (err) {
      Toast.fire({ icon: "error", title: "Checkout Failed" });
    }
  };

  // Handle Record Deletion
  const handleDelete = async (id) => {
    try {
      const res = await Swal.fire({
        title: "Are you sure?",
        text: "This record will be permanently removed!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!"
      });

      if (res.isConfirmed) {
        await deleteDoc(doc(db, "guestEnquiries", id));
        fetchEnquiries();
        Toast.fire({ icon: "success", title: "Deleted Successfully" });
      }
    } catch (err) {
      Toast.fire({ icon: "error", title: "Delete Failed" });
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      let finalPhoto = imgSrc;
      if (imgSrc?.startsWith("data:image")) {
        const storageRef = ref(storage, `visitors/${Date.now()}.jpg`);
        await uploadString(storageRef, imgSrc, "data_url");
        finalPhoto = await getDownloadURL(storageRef);
      }

      const payload = {
        ...form,
        visitingTarget: targetType,
        studentName: foundStudent ? `${foundStudent.student_name} (${foundStudent.class_name})` : form.guestName,
        visitorPhoto: finalPhoto,
        verified: isVerified,
        updatedAt: Timestamp.now()
      };

      if (editId) { await updateDoc(doc(db, "guestEnquiries", editId), payload); }
      else { 
        await addDoc(collection(db, "guestEnquiries"), { ...payload, createdAt: Timestamp.now() }); 
      sendSingleWhatsApp(form.mobile);
      }

      setOpen(false);
      resetForm();
      fetchEnquiries();
      Toast.fire({ icon: "success", title: "Visitor Logged" });
    } catch (e) { Toast.fire({ icon: "error", title: "Action Failed" }); }
    setLoading(false);
  };

  const filteredEnquiries = enquiries.filter(e =>
    e.guestName?.toLowerCase().includes(searchQuery.toLowerCase()) || e.mobile?.includes(searchQuery)
  );

  const formatTimeString = (timeStr) => {
    if (!timeStr || !timeStr.includes(':')) return timeStr;

    // Split hours and minutes
    let [hours, minutes] = timeStr.split(':');
    hours = parseInt(hours);

    // Determine AM/PM
    const ampm = hours >= 12 ? 'PM' : 'AM';

    // Convert to 12-hour format
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'

    return `${hours}:${minutes} ${ampm}`;
  };

   const sendSingleWhatsApp = async (mobile) => {
  
          try {
  
              const walletDoc = await getDoc(doc(db, "wallets", "0YVnAJmiq8cajkf4ufE5a8xYoFQ2")); // Adjust ID as per your DB
              const totalMessages = walletDoc.exists() ? walletDoc.data().totalMessages : 0;
  
              // Fetch the count of sent messages (more efficient than getDocs)
              const historyRef = collection(db, "whatsapphistory");
              const historyQuery = query(historyRef);
              const historySnapshot = await getCountFromServer(historyQuery);
              const sentCount = historySnapshot.data().count;
              const remaining = totalMessages - sentCount;
              if (remaining < 0) {
                  Swal.fire("Error", "Whatsapp Wallet Balance is Low", "error");
  
                  return
              }
  
              // 🔥 FETCH WHATSAPP CONFIG FROM FIRESTORE
              const whatsappSnap = await getDocs(collection(db, "whatsapp"));
  
              if (whatsappSnap.empty) {
                  return;
              }
  
              const whatsappData = whatsappSnap.docs[0].data();
              const wabaid = whatsappData.phoneid;       // ✅ dynamic
              const accesstoken = whatsappData.accesstoken; // ✅ dynamic
              let success = 0;
  
              let status = "failed";
  
              const payload = {
                  messaging_product: "whatsapp",
                  to: `91${mobile}`,
                  type: "template",
                  template: {
                      name: "admission_enquiry",
                      language: { code: "en" },
                      components: [
                          {
                              type: "body",
                              parameters: [],
                          }
                      ],
                  },
              };
  
              try {
                  const r = await fetch(
                      `https://graph.facebook.com/v22.0/${wabaid}/messages`,
                      {
                          method: "POST",
                          headers: {
                              "Content-Type": "application/json",
                              Authorization: `Bearer ${accesstoken}`,
                          },
                          body: JSON.stringify(payload),
                      }
                  );
  
                  const res = await r.json();
  
                  if (res.messages?.[0]?.id) {
                      status = "sent";
                      success++;
                  } else {
                      console.log("WhatsApp Error:", res);
                  }
              } catch (err) {
                  console.log("Fetch Error:", err);
                  status = "failed";
              }
  
              // 🔥 SAVE HISTORY
              await addDoc(collection(db, "whatsapphistory"), {
                  mobile: mobile,
                  postTitle: "Guest Enquiry",
                  type: "template",
                  status,
                  timestamp: new Date().toISOString(),
              });
  
              await new Promise((res) => setTimeout(res, 300));
  
          } catch (err) {
              console.log(err);
          }
  
      };

  return (
    <Box p={2} bgcolor="#f4f7f6" minHeight="100vh">
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" mb={2} spacing={2}>
        <Box>
          <Typography variant="h5" fontWeight="bold" color="teal">Gate Management</Typography>
          <Typography variant="caption" color="text.secondary">Visitor Verification & Logging</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <TextField size="small" placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} sx={{ bgcolor: 'white' }} />
          <Button variant="contained" onClick={() => { resetForm(); setOpen(true); }} sx={{ bgcolor: "teal" }}>New Entry</Button>
        </Stack>
      </Stack>

      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: "#fafafa" }}>
            <TableRow>
              <TableCell>Sr</TableCell>
              <TableCell>Photo</TableCell>
              <TableCell>Visitor</TableCell>
              <TableCell>Visiting For</TableCell>
              <TableCell>Date/Time</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredEnquiries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
              <TableRow key={row.id} sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}>
                <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                <TableCell><Avatar src={row.visitorPhoto} variant="rounded" sx={{ width: 45, height: 45 }} /></TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">{row.guestName}</Typography>
                  <Typography variant="caption" color="text.secondary">{row.mobile}</Typography>
                </TableCell>
                <TableCell>
                  {/* <Chip label={row.visitingTarget} size="small" sx={{ fontSize: 10, height: 18, mb: 0.5 }} /> */}
                  <Typography variant="caption" display="block" sx={{ lineHeight: 1 }}>{row.visitingTarget}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" display="block" sx={{ fontWeight: 600 }}>
                    <b style={{ color: '#2e7d32' }}>In: </b>
                    {row.inTime ? formatTimeString(row.inTime) : '--:--'}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ fontWeight: 600 }}>
                    <b style={{ color: '#d32f2f' }}>Out: </b>
                    {row.outTime ? formatTimeString(row.outTime) : '--:--'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={row.status}
                    size="small"
                    color={row.status === "Completed" ? "success" : "warning"}
                    variant="outlined"
                    sx={{ fontWeight: 'bold', fontSize: 10 }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Stack direction="row" spacing={0.5} justifyContent="center">
                    {/* Checkout Button - Only show if In-Progress */}
                    {row.status !== "Completed" && (
                      <Tooltip title="Check Out">
                        <IconButton size="small" color="success" onClick={() => handleCheckout(row.id)}>
                          <VerifiedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}

                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => {
                          setEditId(row.id);
                          // 1. Restore the main form fields
                          setForm({
                            mobile: row.mobile || "",
                            guestName: row.guestName || "",
                            address: row.address || "",
                            reason: row.reason || "",
                            inTime: row.inTime || "",
                            outTime: row.outTime || "",
                            status: row.status || "In-Progress"
                          });
                          // 2. Restore UI-specific states
                          setTargetType(row.visitingTarget || "Student");
                          setImgSrc(row.visitorPhoto || null);
                          setIsVerified(row.verified || false);

                          // 3. If it's a student, we might want to show the student name info 
                          // (Note: If you want the full 'Verified Family Photos' card back, 
                          // you'd need to re-fetch handleVerifyMobile or store the object in the log)

                          setOpen(true);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination component="div" count={filteredEnquiries.length} rowsPerPage={rowsPerPage} page={page} onPageChange={(e, n) => setPage(n)} />
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ bgcolor: "teal", color: "white", p: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{editId ? "Update Entry" : "Visitor Registration"}</Typography>
            <IconButton onClick={() => setOpen(false)} sx={{ color: "white" }}><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 2, mt: 2 }}>
          <Grid className="row" spacing={2}>
            {/* Left side: Visitor Verification */}
            <Grid className="col-sm-8" item xs={12} md={7}>
              <Stack spacing={2}>
                <TextField select label="Target" fullWidth size="small" value={targetType} onChange={(e) => setTargetType(e.target.value)}>
                  {["Student", "Chairman", "Principal", "Teacher", "Staff", "Other"].map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </TextField>

                <TextField label="Visitor Mobile" fullWidth size="small" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} />

                {targetType === "Student" && !editId && (
                  <Box p={1.5} border="1px solid #ddd" borderRadius={2} bgcolor="#f0f7f7">
                    <Typography variant="caption" fontWeight="bold" color="teal">Student Link Verification</Typography>
                    <Stack direction="row" spacing={1} mt={1}>
                      <TextField select label="Rel" sx={{ width: '35%' }} size="small" value={relationType} onChange={(e) => setRelationType(e.target.value)}>
                        {["Father", "Mother", "Guardian"].map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                      </TextField>
                      <Button variant="contained" onClick={handleVerifyMobile} sx={{ bgcolor: "teal" }} disabled={verifying}>
                        {verifying ? <CircularProgress size={20} color="inherit" /> : <WhatsAppIcon />}
                      </Button>
                    </Stack>
                    {otpSent && (
                      <Stack direction="row" spacing={1} mt={1}>
                        <TextField size="small" label="OTP" fullWidth value={otpInput} onChange={(e) => setOtpInput(e.target.value)} />
                        <Button variant="contained" color="success" onClick={verifyOTP}>Verify</Button>
                      </Stack>
                    )}

                  </Box>
                )}
                <TextField
                  label="Visitor Name"
                  fullWidth
                  size="small"
                  value={form.guestName}
                  onChange={(e) => setForm({ ...form, guestName: e.target.value })}
                />
                <TextField label="Reason / Address" fullWidth size="small" multiline rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />

                <Stack direction="row" spacing={2}>
                  <TextField
                    label="In Time"
                    type="time"
                    fullWidth
                    size="small"
                    value={form.inTime}
                    onChange={(e) => setForm({ ...form, inTime: e.target.value })} // Enabled editing
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="Out Time"
                    type="time"
                    fullWidth
                    size="small"
                    value={form.outTime}
                    onChange={(e) => setForm({ ...form, outTime: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Stack>

              </Stack>
            </Grid>

            {/* Right side: Live Photo */}
            <Grid className="col-sm-4" item xs={12} md={5} textAlign="center">
              <Typography variant="caption" color="text.secondary">Live Visitor Capture</Typography>
              <Avatar src={imgSrc} variant="rounded" sx={{ width: 200, height: 200, border: '2px solid teal', mx: 'auto', mb: 1 }} />
              {!editId && <Button variant="outlined" size="small" startIcon={<PhotoCameraIcon />} onClick={() => setShowWebcam(true)}>Capture Photo</Button>}
              {showWebcam && (
                <Box mt={1} p={1} bgcolor="#000" borderRadius={1}>
                  <Webcam ref={webcamRef} screenshotFormat="image/jpeg" width="100%" />
                  {/* <Button variant="contained" color="error" fullWidth onClick={() => { setImgSrc(webcamRef.current.getScreenshot()); setShowWebcam(false); }}>Capture Now</Button> */}
                </Box>
              )}
              {foundStudent && (
                <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: '#fff', borderRadius: 2, border: '1px solid teal' }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar
                      src={foundStudent.studentPhoto}
                      sx={{ width: 80, height: 80, border: '2px solid #e0f2f1' }}
                      variant="rounded"
                    >
                      <SchoolIcon sx={{ fontSize: 40 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold" color="teal">
                        {foundStudent.studentFullName}
                      </Typography>
                      <Chip
                        label={foundStudent.resolvedClassName}
                        size="small"
                        sx={{ bgcolor: 'teal', color: 'white', fontWeight: 'bold', mt: 0.5 }}
                      />
                      {/* <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                        Adm No: {foundStudent.admission_no}
                      </Typography> */}
                    </Box>
                  </Stack>

                  <Divider sx={{ my: 1.5 }} />

                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                    VERIFIED FAMILY PHOTOS
                  </Typography>
                  <Grid container spacing={1} mt={0.5}>
                    <Grid item xs={4} textAlign="center">
                      <Avatar src={foundStudent.father_photo} variant="square" sx={{ width: 50, height: 55, mx: 'auto', borderRadius: 1 }} />
                      <Typography sx={{ fontSize: '0.6rem', mt: 0.5 }}>FATHER</Typography>
                    </Grid>
                    <Grid item xs={4} textAlign="center">
                      <Avatar src={foundStudent.mother_photo} variant="square" sx={{ width: 50, height: 55, mx: 'auto', borderRadius: 1 }} />
                      <Typography sx={{ fontSize: '0.6rem', mt: 0.5 }}>MOTHER</Typography>
                    </Grid>
                    {foundStudent.guardians?.[0] && (
                      <Grid item xs={4} textAlign="center">
                        <Avatar src={foundStudent.guardians[0].photo} variant="square" sx={{ width: 50, height: 55, mx: 'auto', borderRadius: 1 }} />
                        <Typography sx={{ fontSize: '0.6rem', mt: 0.5 }}>GUARDIAN</Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              )}
            </Grid>
          </Grid>
          <Button
            fullWidth
            variant="contained"
            size="large"
            sx={{ mt: 2, bgcolor: "teal" }}
            onClick={handleSubmit}
            disabled={loading || (targetType === "Student" && !isVerified && !editId)} // Added !editId
          >
            {loading ? "Processing..." : (editId ? "UPDATE LOG" : "COMPLETE CHECK-IN")}
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default VisitorManagement;